import type { Conversation, ConversationCommonItem, ConversationRow, UserAuth } from '@repo/common';
import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { ValidationUtils } from '@repo/toolslib/src/utils/validation.util';
import type { BaseModelClass } from '../../lib/db/core.lite';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';

export class ConversationClientAdapter extends PrivateClientDatabaseAdapter<
  ConversationRow,
  ConversationCommonItem
> {
  constructor(
    sqliteModel: BaseModelClass<Conversation, ConversationRow>,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
    }>
  ) {
    super(sqliteModel, 'conversation', userId, authToken, adapterConfig);
  }

  protected sqliteRowToItem(row: ConversationRow): ConversationCommonItem {
    return {
      id: row.id,
      name: row.name,
      editedName: row.editedName != null ? row.editedName : undefined,
      type: row.type,
      userId: this.userId, // Use injected userId since SQLite doesn't have it
      createdAt: new Date(row.createdAt),
      updatedAt: new Date(row.updatedAt),
      deletedAt: row.deletedAt != null ? new Date(row.deletedAt) : undefined,
      transcriptId: row.transcriptId != null ? row.transcriptId : undefined,
      memoryId: row.memoryId != null ? row.memoryId : undefined,
      workspaceId: row.workspaceId != null ? row.workspaceId : undefined,
      isFavorite: row.isFavorite === 1 || row.isFavorite === true,
      tags: row.tags ? parse(row.tags) : undefined,
      data: row.data != null ? parse(row.data) : undefined
    };
  }

  protected itemToSqliteRow(item: ConversationCommonItem): ConversationRow {
    return {
      id: item.id,
      name: item.name || '',
      editedName: item.editedName,
      type: item.type || '',
      createdAt: item.createdAt.toISOString(),
      updatedAt: item.updatedAt.toISOString(),
      deletedAt: item.deletedAt ? item.deletedAt.toISOString() : undefined,
      transcriptId: item.transcriptId ?? undefined,
      memoryId: item.memoryId ?? undefined,
      workspaceId: item.workspaceId ?? undefined,
      isFavorite: item.isFavorite ? 1 : 0,
      tags: item.tags ? stringify(item.tags) : undefined,
      data: item.data ? stringify(item.data) : undefined
    };
  }

  protected async getAllFromSqlite(): Promise<ConversationRow[]> {
    const rows = await (this.sqliteModel as any).findAllRows({ paranoid: true });
    return rows.map((row: any) => this.normalizeRow(row));
  }

  protected async getFromSqliteById(id: string): Promise<ConversationRow | null> {
    const conversations = await (this.sqliteModel as any).findAllRows({
      where: { id },
      limit: 1
    });

    if (conversations.length === 0) return null;

    return this.normalizeRow(conversations[0]);
  }

  private normalizeRow(row: any): ConversationRow {
    const toIso = (v: any): string => (typeof v === 'string' ? v : new Date(v).toISOString());
    const toMaybeString = (v: any): string | undefined =>
      v == null ? undefined : typeof v === 'string' ? v : stringify(v);

    return {
      id: row.id,
      name: row.name,
      editedName: row.editedName ?? undefined,
      type: row.type,
      createdAt: toIso(row.createdAt),
      updatedAt: toIso(row.updatedAt),
      deletedAt: row.deletedAt ? toIso(row.deletedAt) : undefined,
      transcriptId: row.transcriptId ?? undefined,
      memoryId: row.memoryId ?? undefined,
      workspaceId: row.workspaceId ?? undefined,
      isFavorite: row.isFavorite === 1 || row.isFavorite === true ? 1 : 0,
      tags: toMaybeString(row.tags),
      data: toMaybeString(row.data)
    } as ConversationRow;
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<ConversationCommonItem> {
    return {
      type: 'none' as const,
      getKey: (item: ConversationCommonItem) => item.id
    };
  }

  /**
   * Override validateItemEquality to use the improved validateConversationEquality from ValidationUtils
   * This provides more accurate comparison for conversation-specific fields and data structures
   */
  protected validateItemEquality(
    item1: ConversationCommonItem,
    item2: ConversationCommonItem
  ): { isEqual: boolean; differences: string[] } {
    return ValidationUtils.validateConversationEquality(
      item1 as ConversationCommonItem,
      item2 as ConversationCommonItem
    );
  }
}
